/**
 * Колонки таблиц отчёта: объединённые представления параметров.
 */

import { formatSignificantFiguresOrDash } from './formatSignificantFigures.js';

/** Ключ виртуальной колонки «суммарная доза / доза на сеанс / число фракций» */
export const REPORT_COL_DOSE_D_D_NF = 'D / d / n_f';

/** Параметры фракционирования — выносятся в блок «Общая информация», не в расчётную таблицу. */
export const FRACTIONATION_PARAM_KEYS = new Set([
  'totalDose',
  'total_absorbed_dose',
  'n_f',
  'number_of_fractions',
  'dose_per_fraction',
  'D',
  'd',
]);

/** Технические ключи result, не показываемые в отчёте. */
export const REPORT_HIDDEN_RESULT_KEYS = new Set([
  'per_stage',
  'structure_name',
]);

function fmtReportNumber(v) {
  return formatSignificantFiguresOrDash(v);
}

/**
 * Убирает totalDose и n_f из списка ключей и при необходимости добавляет одну колонку REPORT_COL_DOSE_D_D_NF.
 * @param {string[]} keys
 * @returns {string[]}
 */
export function mergeDoseFractionationParamKeys(keys) {
  const arr = keys.filter(Boolean);
  const hadTotal = arr.includes('totalDose');
  const hadNf = arr.includes('n_f');
  if (!hadTotal && !hadNf) {
    return [...arr].sort();
  }
  const set = new Set(arr.filter((k) => k !== 'totalDose' && k !== 'n_f'));
  set.add(REPORT_COL_DOSE_D_D_NF);
  return Array.from(set).sort();
}

/**
 * Строка для колонки D / d / n_f (Гр / Гр / безразмерно).
 * @param {Record<string, unknown>|null|undefined} params
 * @returns {string}
 */
export function formatDoseSessionColumn(params) {
  if (!params || typeof params !== 'object') return '—';
  const rawD = params.totalDose ?? params.total_absorbed_dose;
  const rawNf = params.n_f ?? params.number_of_fractions;
  const D = rawD !== undefined && rawD !== null && rawD !== '' ? Number(rawD) : NaN;
  const nf = rawNf !== undefined && rawNf !== null && rawNf !== '' ? Number(rawNf) : NaN;
  const d =
    Number.isFinite(D) && Number.isFinite(nf) && nf !== 0 ? D / nf : null;
  return `${fmtReportNumber(Number.isFinite(D) ? D : null)} / ${fmtReportNumber(d)} / ${fmtReportNumber(Number.isFinite(nf) ? nf : null)}`;
}

export function formatReportNumber(v) {
  return fmtReportNumber(v);
}

/**
 * Ключи params модели (без фракционирования) для расчётной таблицы.
 * @param {Array<{ params?: object }>} rows
 * @returns {string[]}
 */
export function collectReportModelParamKeys(rows) {
  const set = new Set();
  (rows || []).forEach((r) => {
    if (!r.params || typeof r.params !== 'object') return;
    Object.keys(r.params).forEach((k) => {
      if (!FRACTIONATION_PARAM_KEYS.has(k)) set.add(k);
    });
  });
  return Array.from(set).sort();
}

/** Подписи показателей, как в таблице результатов расчёта. */
const REPORT_RESULT_COLUMN_LABELS = {
  __stage_eqd2__: 'EQD2 этапа',
  __stage_eud__: 'EUD этапа',
  bed: 'BED',
  eqd2: 'EQD2',
  eud: 'EUD',
  eqd2_sum: 'EQD2Σ',
  eud_sum: 'EUDΣ',
  utcp: 'UTCP',
};

/**
 * Заголовок колонки результата. Пустая строка — у ключа нет отдельного обозначения.
 * @param {string} key
 * @param {'TCP'|'NTCP'|string} [tableType]
 * @returns {string}
 */
export function reportResultColumnLabel(key, tableType) {
  if (REPORT_RESULT_COLUMN_LABELS[key]) return REPORT_RESULT_COLUMN_LABELS[key];
  if (key === 'result') return tableType || '';
  return '';
}

function resultHasValue(rows, key) {
  return (rows || []).some((row) => {
    const src = row.adaptiveRtMergedResult || row.result;
    return src && typeof src === 'object' && src[key] != null && src[key] !== '';
  });
}

/**
 * @param {Array<{ result?: object, adaptiveRtMergedResult?: object, isAdaptiveRtStage?: boolean }>} rows
 * @param {{ hasAdaptiveStages: boolean }} options
 * @returns {string[]}
 */
export function collectReportSummaryResultKeys(rows, { hasAdaptiveStages }) {
  if (hasAdaptiveStages) {
    return ['__stage_eqd2__', '__stage_eud__', 'eqd2_sum', 'eud_sum', 'result'];
  }
  const keys = [];
  ['bed', 'eqd2', 'eud'].forEach((key) => {
    if (resultHasValue(rows, key)) keys.push(key);
  });
  keys.push('result');
  if (resultHasValue(rows, 'utcp')) keys.push('utcp');
  return keys;
}
